import { jsPDF } from 'jspdf';

const drawWheel = (doc, domains, centerX, centerY, radius) => {
  const segments = domains.length;
  const anglePerSegment = (2 * Math.PI) / segments;
  
  // Draw background circles
  for (let i = 0; i <= 10; i++) {
    doc.setDrawColor(i === 10 ? 180 : 240);
    doc.setLineWidth(i === 10 ? 0.5 : 0.2);
    doc.circle(centerX, centerY, (radius / 10) * i, 'S');
  }
  
  // Draw radial lines and filled segments
  domains.forEach((domain, index) => {
    const angle = anglePerSegment * index - Math.PI / 2;
    const nextAngle = angle + anglePerSegment;
    const scoreRadius = (radius / 10) * domain.score;
    
    // Draw radial line
    doc.setDrawColor(220);
    doc.setLineWidth(0.2);
    doc.line(
      centerX,
      centerY,
      centerX + radius * Math.cos(angle),
      centerY + radius * Math.sin(angle)
    );
    
    // Draw filled segment if score > 0
    if (domain.score > 0) {
      // Convert hex color to RGB
      const hex = domain.color.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      
      doc.setFillColor(r, g, b);
      doc.setDrawColor(r, g, b);
      doc.setLineWidth(1);
      
      // Draw the segment as a filled triangle/arc approximation
      const steps = 20;
      const stepAngle = anglePerSegment / steps;
      
      for (let step = 0; step < steps; step++) {
        const a1 = angle + stepAngle * step;
        const a2 = angle + stepAngle * (step + 1);
        
        doc.triangle(
          centerX,
          centerY,
          centerX + scoreRadius * Math.cos(a1),
          centerY + scoreRadius * Math.sin(a1),
          centerX + scoreRadius * Math.cos(a2),
          centerY + scoreRadius * Math.sin(a2),
          'FD'
        );
      }
    }
  });
  
  // Draw labels
  doc.setFontSize(7);
  domains.forEach((domain, index) => {
    const angle = anglePerSegment * index - Math.PI / 2;
    const labelRadius = radius + 12;
    const x = centerX + labelRadius * Math.cos(angle);
    const y = centerY + labelRadius * Math.sin(angle);
    
    doc.setTextColor(0);
    const displayName = domain.customName || domain.name;
    const words = displayName.split(' ');
    if (words.length > 2) {
      doc.text(words.slice(0, 2).join(' '), x, y - 1, { align: 'center' });
      doc.text(words.slice(2).join(' '), x, y + 2, { align: 'center' });
    } else {
      doc.text(displayName, x, y, { align: 'center' });
    }
  });
};

export const generateAssessmentPDF = (domains, userName = 'User') => {
  const doc = new jsPDF();
  
  // Add logo/title
  doc.setFontSize(20);
  doc.setTextColor(22, 163, 74); // green-600
  doc.text('Life Worth Funding', 105, 20, { align: 'center' });
  
  doc.setFontSize(16);
  doc.setTextColor(0, 0, 0);
  doc.text('Life Wheel Assessment Results', 105, 30, { align: 'center' });
  
  // Add date
  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  const today = new Date().toLocaleDateString();
  doc.text(`Date: ${today}`, 105, 37, { align: 'center' });
  
  // Add tagline
  doc.setFontSize(12);
  doc.setTextColor(60, 60, 60);
  doc.text('A life worth living is a life worth funding', 105, 45, { align: 'center' });
  
  // Calculate average score
  const avgScore = (domains.reduce((sum, d) => sum + d.score, 0) / domains.length).toFixed(1);
  
  // Add average score
  doc.setFontSize(14);
  doc.setTextColor(22, 163, 74);
  doc.text(`Overall Balance Score: ${avgScore}/10`, 105, 55, { align: 'center' });
  
  // Add line separator
  doc.setDrawColor(200, 200, 200);
  doc.line(20, 60, 190, 60);
  
  // Draw the wheel chart
  drawWheel(doc, domains, 105, 130, 50);
  
  // Add scores table on second page
  doc.addPage();
  let yPosition = 20;
  
  doc.setFontSize(14);
  doc.setTextColor(0, 0, 0);
  doc.text('Detailed Scores', 105, yPosition, { align: 'center' });
  yPosition += 10;
  
  doc.setDrawColor(200, 200, 200);
  doc.line(20, yPosition, 190, yPosition);
  yPosition += 10;
  
  doc.setFontSize(10);
  domains.forEach((domain, index) => {
    // Domain name
    doc.setTextColor(0, 0, 0);
    doc.text(`${index + 1}. ${domain.customName || domain.name}`, 25, yPosition);
    
    // Score with color indicator
    const scorePercent = (domain.score / 10) * 100;
    if (scorePercent >= 70) {
      doc.setTextColor(34, 197, 94); // green
    } else if (scorePercent >= 40) {
      doc.setTextColor(251, 191, 36); // yellow
    } else {
      doc.setTextColor(239, 68, 68); // red
    }
    doc.text(`${domain.score}/10`, 170, yPosition);
    
    yPosition += 8;
    
    // Add new page if needed
    if (yPosition > 270) {
      doc.addPage();
      yPosition = 20;
    }
  });
  
  // Add insights section
  yPosition += 10;
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }
  
  doc.setDrawColor(200, 200, 200);
  doc.line(20, yPosition, 190, yPosition);
  yPosition += 10;
  
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text('Areas for Growth:', 20, yPosition);
  yPosition += 8;
  
  // Find lowest scoring domains
  const lowestDomains = [...domains]
    .sort((a, b) => a.score - b.score)
    .slice(0, 3);
  
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  lowestDomains.forEach((domain, index) => {
    doc.text(`${index + 1}. ${domain.customName || domain.name} (${domain.score}/10)`, 25, yPosition);
    yPosition += 6;
  });
  
  yPosition += 10;
  doc.text('Focus on these areas to improve your overall life balance.', 25, yPosition);
  
  // Add Reflection Section
  yPosition += 15;
  if (yPosition > 240) {
    doc.addPage();
    yPosition = 20;
  }
  
  doc.setDrawColor(200, 200, 200);
  doc.line(20, yPosition, 190, yPosition);
  yPosition += 10;
  
  doc.setFontSize(14);
  doc.setTextColor(22, 163, 74);
  doc.text('Analysis & Reflection', 105, yPosition, { align: 'center' });
  yPosition += 8;
  
  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.text('Take time to reflect on these questions:', 105, yPosition, { align: 'center' });
  yPosition += 10;
  
  const reflectionQuestions = [
    'What patterns do I notice in my life balance?',
    'Which areas bring me the most joy and fulfillment?',
    'What areas feel most out of alignment right now?',
    'What is one small step I can take this week to improve my lowest-scoring domain?',
    'What strengths from my highest-scoring areas can I leverage in other domains?',
    'What support or resources do I need to create more balance?',
    'What would my life look like if all domains were at a 9 or 10?',
    'What am I willing to commit to changing in the next 30 days?'
  ];
  
  reflectionQuestions.forEach((question, index) => {
    if (yPosition > 265) {
      doc.addPage();
      yPosition = 20;
    }
    
    doc.setFontSize(10);
    doc.setTextColor(0, 0, 0);
    doc.text(`${index + 1}. ${question}`, 25, yPosition, { maxWidth: 160 });
    yPosition += 12;
    
    // Add space for writing
    doc.setDrawColor(220, 220, 220);
    doc.line(25, yPosition, 185, yPosition);
    doc.line(25, yPosition + 5, 185, yPosition + 5);
    yPosition += 12;
  });
  
  // Add footer
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.text(`Page ${i} of ${pageCount}`, 105, 287, { align: 'center' });
    doc.text('Life Worth Funding - www.lifeworthfunding.com', 105, 292, { align: 'center' });
  }
  
  // Save the PDF
  const fileName = `Life-Wheel-Assessment-${today.replace(/\//g, '-')}.pdf`;
  doc.save(fileName);
  
  return fileName;
};
